import React from 'react'
import { Link } from 'react-router-dom'
import styles from './Overview.scss'
import { challengeMessages } from 'features/challenges/intlMessages'
import { isEmpty } from 'features/challenges/challengeUtils'
import { messages as orgChallengeMessages } from 'features/organizations/OrganizationChallenges'
import { ChallengeMapCard } from '../../ChallengeMap'
import { DetailsCard, Settings } from '../../DetailsCard'
import { Leaderboard } from '../../Leaderboard'
import { MilestonesCard } from '../../MilestonesCard'
import { Rules } from '../../Rules'
import { QuickStats } from 'features/experiences/QuickStats'
import { messages as headerMessages } from 'features/ride_reports/EditPage/Header'
import { Card } from 'common/styleguide/Card'
import { SectionBox } from 'common/styleguide/SectionBox'
import { CTA } from 'common/styleguide/CTA'
import { CircledIcon } from 'common/CircledIcon'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/SideNavLayout/TwoCol'
import { SHARE_MODAL } from 'common/modals/ShareModal'
import { useFormatMessage, featureNames, commonVerbs } from 'lib/localization'
import { showModal } from 'lib/modalUtils'
import { urlWithPrivacyCode } from 'lib/utils'
import { c } from 'lib/textUtils'
import { defineMessages } from 'react-intl'

export const messages = defineMessages({
  settingsEmpty: {
    id: 'Overview.settingsEmpty',
    defaultMessage: 'Configure the Challenge date, time, criteria, and format.'
  },
  viewSettings: {
    id: 'Overview.viewSettings',
    defaultMessage: 'View Settings'
  },
  milestonesEmpty: {
    id: 'Overview.milestonesEmpty',
    defaultMessage: 'Celebrate participants’ progress along the way.'
  },
  viewMilestones: {
    id: 'Overview.viewMilestones',
    defaultMessage: 'View Milestones'
  },
  viewParticipants: {
    id: 'Overview.viewParticipants',
    defaultMessage: 'View Participants'
  }
})

export const Overview = ({ goal, metaForGoal, milestones, ...leaderboardProps }) => {
  const fm = useFormatMessage()
  const commonProps = { goal, metaForGoal, fm }

  return (
    <ColWrap>
      <MainCol>
        <DetailsCard {...commonProps} />
        {goal.goalParams.route_id && <ChallengeMapCard
          routeId={goal.get('goalParams').route_id}
          milestones={milestones}
          amountCompleted={0}
          participationType='community'
        />}
        <Leaderboard
          {...commonProps}
          {...leaderboardProps}
          hideLoadMore
        />
      </MainCol>
      <SecondaryCol>
        <Card>
          <CTA
            onClick={() => showModal(SHARE_MODAL, {
              objectId: goal.id,
              objectType: 'goal',
              objectTitle: goal.name,
              objectUrl: urlWithPrivacyCode(`/challenges/${goal.id}`, goal.privacyCode)
            })}
            margins={false}>
            {fm(commonVerbs.share)}
          </CTA>
          <CTA link to={`/challenges/${goal.id}?preview=true`} margins={false} style={{ paddingBottom: '0px', marginTop: '8px' }}>{c(fm(headerMessages.preview))}</CTA>
        </Card>
        <SettingsCard {...commonProps} />
        <AdminMilestones {...commonProps} milestones={milestones} />
        <QuickStats
          totals={metaForGoal.participants}
          title={fm(challengeMessages.participants)}
          to={`/challenges/${goal.id}/participants`}
          link={fm(messages.viewParticipants)}
          bottomMargin
          rows={2}
          labelsUnder
        />
        <Rules goal={goal} />
      </SecondaryCol>
    </ColWrap>
  )
}

const SettingsCard = props => {
  const { goal, fm } = props
  if (isEmpty(goal, fm(orgChallengeMessages.defaultName))) {
    return <SecondaryColCard
      title={fm(featureNames.settings)}
      top={<div className={styles.emptyCard}>
        <div className={styles.emptyTop}>
          <CircledIcon i='menu-settings-icon' size={40} />
          {fm(messages.settingsEmpty)}
        </div>
      </div>}
      to={`/challenges/${goal.id}/settings`}
      link={fm(messages.viewSettings)}
    />
  }

  return <SectionBox title={fm(featureNames.settings)}>
    <Settings {...props} />
  </SectionBox>
}

const AdminMilestones = props => {
  const { goal, milestones, fm } = props
  if (milestones.size === 0) {
    return <SecondaryColCard
      title={fm(featureNames.milestones)}
      top={<div className={styles.emptyCard}>
        <div className={styles.emptyTop}>
          <CircledIcon i='md-trophy' size={40} />
          {fm(messages.milestonesEmpty)}
        </div>
      </div>}
      to={`/challenges/${goal.id}/settings`}
      link={fm(messages.viewMilestones)}
    />
  }

  return <MilestonesCard {...props} />
}

export const SecondaryColCard = ({ title, top, to, link }) => {
  return <SectionBox title={title} className={styles.secondaryColCard}>
    {top}
    <Link to={to}>{link}</Link>
  </SectionBox>
}
